import React, { useEffect, useState } from 'react';
import { Clock, Activity, FileText, User, Building, LogIn, Upload, Edit, Bot } from 'lucide-react';
import api from '../../utils/api';
import { formatDistanceToNow } from 'date-fns';

const SystemLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            const { data } = await api.get('/logs?limit=10');
            if (data.success) {
                setLogs(data.data);
            }
        } catch (error) {
            console.error('Failed to fetch logs', error);
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (log) => {
        const { action, module, type } = log;
        if (action.includes('Login')) return <LogIn className="w-4 h-4 text-green-500" />;
        if (action.includes('Upload')) return <Upload className="w-4 h-4 text-blue-500" />;
        if (action.includes('Create')) return <Activity className="w-4 h-4 text-purple-500" />;
        if (action.includes('Update')) return <Edit className="w-4 h-4 text-orange-500" />;
        if (action.includes('Delete')) return <FileText className="w-4 h-4 text-red-500" />;

        if (module === 'College' || type === 'college') return <Building className="w-4 h-4 text-indigo-500" />;

        switch (module) {
            case 'Auth': return <User className="w-4 h-4 text-gray-500" />;
            case 'College': return <Building className="w-4 h-4 text-indigo-500" />;
            default: return <Activity className="w-4 h-4 text-gray-400" />;
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                {[1, 2, 3].map(i => (
                    <div key={i} className="flex space-x-3 animate-pulse">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (logs.length === 0) {
        return <p className="text-sm text-gray-500 text-center py-4">No system activity logs found.</p>;
    }

    return (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {logs.map((log) => (
                <div key={log._id} className="group flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-all duration-200 border-l-2 border-transparent hover:border-gray-300">
                    <div className="relative mt-1">
                        <div className="p-1.5 bg-gray-100 rounded-md flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all">
                            {getIcon(log)}
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {log.action}
                                <span className="text-xs font-normal text-gray-500 ml-2 bg-gray-100 px-1.5 py-0.5 rounded">
                                    {log.module}
                                </span>
                            </p>
                            <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                            </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{log.details}</p>
                        <div className="mt-1 flex items-center text-[10px] text-gray-400">
                            <User className="w-3 h-3 mr-1" />
                            {log.user ? (
                                <span className="font-medium text-gray-600">{log.user.fullName}</span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-gray-500 font-medium">
                                    <Bot className="w-3 h-3" /> System
                                </span>
                            )}
                            {log.user?.role && <span className="ml-1 px-1 bg-gray-100 rounded text-gray-500 border border-gray-200">{log.user.role}</span>}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SystemLogs;
